// EnquiriesPanel — admissions enquiries with a simple funnel
// (new → called → visited → admitted / closed), follow-up dates and notes.
import { useEffect, useState } from 'react';
import { Phone, Trash2, Download, PhoneCall, UserPlus } from 'lucide-react';
import { getEnquiries, createEnquiry, updateEnquiry, deleteEnquiry, getCallList, getEnquiryFollowups, addEnquiryFollowup } from '../api/client';
import { Modal, Msg as FormMsg, errText } from '../ui/Form';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { fmtDate, todayISO, downloadCsv } from '../ui/format';

const STATUSES = [
  { key: 'new',      label: 'New',      cls: 'bg-transit-tint text-transit-deep' },
  { key: 'called',   label: 'Called',   cls: 'bg-clearance-tint text-clearance-deep' },
  { key: 'visited',  label: 'Visited',  cls: 'bg-caution-tint text-caution' },
  { key: 'admitted', label: 'Admitted', cls: 'bg-success-tint text-success' },
  { key: 'closed',   label: 'Closed',   cls: 'bg-line-soft text-ink-soft' },
];
const statusCls = Object.fromEntries(STATUSES.map(s => [s.key, s.cls]));
const SOURCES = ['Walk-in', 'Phone', 'Website', 'Referral', 'Other'];
const EMPTY = { student_name: '', guardian_name: '', phone: '', class_sought: '', source: 'Walk-in', follow_up_date: '', notes: '' };

const inputCls = 'border border-line rounded-s px-3 py-2 text-sm bg-paper-raised text-ink focus:outline-none focus:ring-2 focus:ring-transit mt-1';
const labelCls = 'flex flex-col text-xs font-bold text-ink-soft';

export default function EnquiriesPanel({ onAdmit }) {
  const [data,    setData]    = useState(null);
  const [filter,  setFilter]  = useState('');
  const [form,    setForm]    = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [msg,     setMsg]     = useState('');
  const [callList, setCallList] = useState(null); // array when the Call list view is on
  const [logging, setLogging] = useState(null);   // enquiry being called

  useEffect(() => { load(); }, [filter]);

  async function load() {
    getCallList().then(setCallList).catch(() => {});
    try { setData(await getEnquiries(filter && filter !== 'calls' ? filter : undefined)); }
    catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  function startEdit(e) {
    setEditing(e.id);
    setForm({
      student_name: e.student_name, guardian_name: e.guardian_name || '', phone: e.phone,
      class_sought: e.class_sought || '', source: e.source || 'Other',
      follow_up_date: e.follow_up_date || '', notes: e.notes || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function cancel() { setEditing(null); setForm(EMPTY); }

  async function save(e) {
    e.preventDefault();
    try {
      editing ? await updateEnquiry(editing, form) : await createEnquiry(form);
      setMsg(editing ? 'Enquiry updated ✅' : 'Enquiry added ✅');
      cancel(); load();
    } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  async function setStatus(id, status) {
    try { await updateEnquiry(id, { status }); load(); }
    catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  async function remove(en) {
    if (!window.confirm(`Delete the enquiry for ${en.student_name}?`)) return;
    try { await deleteEnquiry(en.id); load(); }
    catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  function exportCsv() {
    downloadCsv('enquiries.csv', [
      { key: 'created', label: 'Date' }, { key: 'student_name', label: 'Student' },
      { key: 'guardian_name', label: 'Parent' }, { key: 'phone', label: 'Phone' },
      { key: 'class_sought', label: 'Class' }, { key: 'source', label: 'Source' },
      { key: 'status', label: 'Status' }, { key: 'follow_up_date', label: 'Follow-up' }, { key: 'notes', label: 'Notes' },
    ], data.rows.map(r => ({ ...r, created: fmtDate(r.created_at) })));
  }

  const counts = data?.counts || {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const today = todayISO();

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Admissions &amp; Enquiries</h2>

      {/* Funnel strip — doubles as the status filter */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-2.5 mb-5">
        <FunnelButton active={filter === 'calls'} onClick={() => setFilter('calls')} label="📞 Call today" value={callList?.length ?? 0} cls="bg-critical-tint text-critical" />
        <FunnelButton active={!filter} onClick={() => setFilter('')} label="All" value={total} cls="bg-paper-raised text-ink" />
        {STATUSES.map(s => (
          <FunnelButton key={s.key} active={filter === s.key} onClick={() => setFilter(s.key)} label={s.label} value={counts[s.key] ?? 0} cls={s.cls} />
        ))}
      </div>

      <div className="grid grid-cols-[minmax(280px,380px)_1fr] gap-5 items-start max-[900px]:grid-cols-1">
        <Card className="p-5">
          <h3 className="font-display font-bold text-[15px] text-ink mb-3">{editing ? 'Edit enquiry' : 'New enquiry'}</h3>
          <form onSubmit={save} className="flex flex-col gap-3">
            <label className={labelCls}>Student name
              <input required className={inputCls} value={form.student_name} onChange={e => setForm({ ...form, student_name: e.target.value })} />
            </label>
            <label className={labelCls}>Parent name
              <input className={inputCls} value={form.guardian_name} onChange={e => setForm({ ...form, guardian_name: e.target.value })} />
            </label>
            <label className={labelCls}>Phone
              <input required type="tel" className={inputCls} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className={labelCls}>Class sought
                <input className={inputCls} placeholder="e.g. 5" value={form.class_sought} onChange={e => setForm({ ...form, class_sought: e.target.value })} />
              </label>
              <label className={labelCls}>Source
                <select className={inputCls} value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                  {SOURCES.map(s => <option key={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <label className={labelCls}>Follow-up date
              <input type="date" className={inputCls} value={form.follow_up_date} onChange={e => setForm({ ...form, follow_up_date: e.target.value })} />
            </label>
            <label className={labelCls}>Notes
              <textarea rows={3} className={inputCls} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </label>
            <div className="flex gap-2">
              <Button type="submit">{editing ? 'Update' : 'Add enquiry'}</Button>
              {editing && <Button type="button" variant="secondary" onClick={cancel}>Cancel</Button>}
            </div>
            {msg && (
              <p className={`text-sm font-bold px-3 py-2 rounded-s w-fit ${msg.startsWith('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>{msg}</p>
            )}
          </form>
        </Card>

        <Card>
          <CardHeader title={filter === 'calls' ? `Call today (${callList?.length ?? 0})` : `Enquiries (${data?.rows.length ?? 0})`}
            action={<Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" disabled={!data?.rows.length} onClick={exportCsv}><Download size={13} /> CSV</Button>} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                  <th className="px-4 py-2.5">Student</th><th className="px-4 py-2.5">Contact</th>
                  <th className="px-4 py-2.5">Class</th><th className="px-4 py-2.5">Follow-up</th>
                  <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {(filter === 'calls' ? (callList || []) : (data?.rows || [])).map(en => {
                  const overdue = en.follow_up_date && en.follow_up_date <= today && !['admitted', 'closed'].includes(en.status);
                  return (
                    <tr key={en.id} className="border-t border-line-soft align-top">
                      <td className="px-4 py-3">
                        <button onClick={() => startEdit(en)} className="font-semibold text-transit-deep hover:underline text-left">{en.student_name}</button>
                        <span className="block text-xs text-ink-soft">{en.source || '—'} · {fmtDate(en.created_at)}</span>
                        {en.notes && <span className="block text-xs text-ink-soft mt-1 max-w-[260px]">{en.notes}</span>}
                      </td>
                      <td className="px-4 py-3">
                        {en.guardian_name && <span className="block">{en.guardian_name}</span>}
                        <a href={`tel:${en.phone}`} className="flex items-center gap-1 text-transit-deep font-semibold hover:underline"><Phone size={12} /> {en.phone}</a>
                      </td>
                      <td className="px-4 py-3">{en.class_sought || '—'}</td>
                      <td className={`px-4 py-3 ${overdue ? 'text-critical font-bold' : ''}`}>{en.follow_up_date ? fmtDate(en.follow_up_date) : '—'}</td>
                      <td className="px-4 py-3">
                        <select value={en.status} onChange={e => setStatus(en.id, e.target.value)} aria-label="Status"
                          className={`text-xs font-bold rounded-full px-2.5 py-1 border-0 cursor-pointer ${statusCls[en.status]}`}>
                          {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button onClick={() => setLogging(en)} title="Log a call" aria-label="Log a call" className="text-transit-deep hover:text-transit p-1"><PhoneCall size={15} /></button>
                        {en.status !== 'admitted' && onAdmit && (
                          <button onClick={() => onAdmit(en)} title="Admit — create the student" aria-label="Admit" className="text-success hover:opacity-80 p-1"><UserPlus size={15} /></button>
                        )}
                        <button onClick={() => remove(en)} aria-label="Delete enquiry" className="text-ink-soft hover:text-critical p-1"><Trash2 size={15} /></button>
                        {en.calls > 0 && <span className="block text-[11px] text-ink-soft">{en.calls} call{en.calls === 1 ? '' : 's'}</span>}
                      </td>
                    </tr>
                  );
                })}
                {filter !== 'calls' && data && !data.rows.length && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-soft">No enquiries {filter ? `marked "${filter}"` : 'yet'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      {logging && <CallLogModal enquiry={logging} onClose={() => setLogging(null)} onSaved={() => { setLogging(null); setMsg('Call logged ✅'); load(); }} />}
    </div>
  );
}

function CallLogModal({ enquiry, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ note: '', next_date: '', status: '' });
  const [msg,  setMsg]  = useState('');
  useEffect(() => { getEnquiryFollowups(enquiry.id).then(setRows).catch(err => setMsg(errText(err))); }, [enquiry.id]);
  async function save(e) {
    e.preventDefault();
    try {
      await addEnquiryFollowup(enquiry.id, { note: form.note, next_date: form.next_date || null, status: form.status || undefined });
      onSaved();
    } catch (err) { setMsg(errText(err)); }
  }
  return (
    <Modal title={`Call log — ${enquiry.student_name}`} onClose={onClose}>
      <p className="text-sm mb-3"><a href={`tel:${enquiry.phone}`} className="inline-flex items-center gap-1 font-bold text-transit-deep"><Phone size={13} /> {enquiry.phone}</a>
        {enquiry.guardian_name ? <span className="text-ink-soft"> · {enquiry.guardian_name}</span> : null}
        {enquiry.class_sought ? <span className="text-ink-soft"> · Class {enquiry.class_sought}</span> : null}</p>
      <form onSubmit={save} className="flex flex-col gap-3">
        <label className={labelCls}>What was discussed
          <textarea required rows={3} className={inputCls} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className={labelCls}>Next follow-up
            <input type="date" className={inputCls} value={form.next_date} onChange={e => setForm({ ...form, next_date: e.target.value })} />
          </label>
          <label className={labelCls}>Status
            <select className={inputCls} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="">Keep ({enquiry.status === 'new' ? 'becomes Called' : enquiry.status})</option>
              {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>
        <Button type="submit" className="w-fit">Save call</Button>
        <FormMsg msg={msg} />
      </form>
      {rows.length > 0 && (
        <div className="mt-4 border-t border-line pt-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-2">Previous calls</h4>
          {rows.map(r => (
            <div key={r.id} className="text-sm py-1.5 border-t border-line-soft first:border-t-0">
              <span className="text-xs text-ink-soft">{fmtDate(r.created_at)}{r.next_date ? ` · next ${fmtDate(r.next_date)}` : ''}</span>
              <p className="text-ink">{r.note}</p>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function FunnelButton({ active, onClick, label, value, cls }) {
  return (
    <button onClick={onClick}
      className={`text-left rounded-m border px-3.5 py-2.5 transition-shadow ${cls} ${active ? 'border-transit ring-2 ring-transit' : 'border-line hover:shadow-1'}`}>
      <span className="block text-[11px] font-bold uppercase tracking-wider opacity-90">{label}</span>
      <span className="font-display font-extrabold text-xl tabular-nums">{value}</span>
    </button>
  );
}
